/**
 * CZNA-1518 — the form's check, slid in from the right over the form: what the check found,
 * grouped by tab, each row a way to its field. Opened by the counts in the pane header
 * (`CheckBadge`), and by itself when Zkontrolovat, Publikovat or Odeslat ke schválení
 * finds something to fix.
 *
 * Over the form rather than beside or above it: a column next to the form did not fit on a
 * laptop next to a larger record, and a banner above it pushed the fields down for as long
 * as it was up. The panel is there while the cataloguer reads the list and gone once they
 * pick a row, so the field it leads to is never under it.
 *
 * Not modal: a click into the form closes it and lands where it was aimed. Escape and the
 * close button close it too, and hand the focus back to whoever opened it (`onClose(true)`).
 */
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { IconButton } from '@folio/stripes/components';
var TONE_COLOR = {
    error: '#b4261f',
    warning: '#8a5d00',
    ok: '#2f7a3a',
};
export var CheckPanel = function (_a) {
    var id = _a.id, open = _a.open, onClose = _a.onClose, tone = _a.tone, heading = _a.heading, note = _a.note, opener = _a.opener, children = _a.children;
    var intl = useIntl();
    var panelRef = useRef(null);
    var closeRef = useRef(null);
    var onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    // Into the panel as it opens, so a keyboard user (and a screen reader) lands on the list
    // the button they pressed produced.
    useEffect(function () {
        var _a;
        if (open)
            (_a = closeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [open]);
    useEffect(function () {
        if (!open)
            return undefined;
        var onKeyDown = function (event) {
            if (event.key === 'Escape')
                onCloseRef.current(true);
        };
        var onPointerDown = function (event) {
            var _a, _b;
            var target = event.target;
            if ((_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            if ((_b = opener === null || opener === void 0 ? void 0 : opener.current) === null || _b === void 0 ? void 0 : _b.contains(target))
                return;
            onCloseRef.current(false);
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        return function () {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open, opener]);
    if (!open)
        return null;
    var titleId = "".concat(id, "-title");
    return (React.createElement("aside", { ref: panelRef, id: id, role: "dialog", "aria-modal": "false", "aria-labelledby": titleId, "data-test-check-panel": true, "data-test-check-panel-tone": tone, style: {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(400px, 100%)',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            borderLeft: '1px solid #d9dde3',
            boxShadow: '-8px 0 28px rgba(31, 35, 41, 0.18)',
            color: '#1f2329',
        } },
        React.createElement("div", { style: {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 8px 8px 16px',
                borderBottom: '1px solid #d9dde3',
            } },
            React.createElement("strong", { id: titleId, style: { flex: 1 } }, intl.formatMessage({ id: 'ui-czna-authorities.validation.checkPanel.title' })),
            React.createElement(IconButton, { ref: closeRef, icon: "times", onClick: function () { return onClose(true); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.validation.checkPanel.close' }), "data-test-check-panel-close": true })),
        React.createElement("div", { role: "status", style: { padding: '10px 16px', borderBottom: '1px solid #d9dde3', display: 'grid', gap: '2px' } },
            React.createElement("strong", { "data-test-check-panel-heading": true, style: { color: TONE_COLOR[tone] } }, heading),
            note && React.createElement("span", { style: { color: '#5d6570', fontSize: '0.93em' } }, note)),
        children && (React.createElement("div", { style: { flex: 1, overflowY: 'auto', padding: '2px 16px 12px' } }, children))));
};
export default CheckPanel;
